import { notify } from "../assets/helpers/toast";
import auth from "./authService";
import { useSocketConnectionStore } from "../stores/socketConnectionStore";
import { useTvSectionStore } from "../stores/tvSectionStore";

let socket = null; // native WebSocket instance

const WS_BASE_URL = process.env.REACT_APP_PUBLIC_WSS; // e.g. wss://api.example.com

const BASE_RECONNECT_MS = 3000;
const MAX_RECONNECT_DELAY_MS = 60000;

let reconnectAttempts = 0;
let reconnectTimer = null;
let manuallyClosed = false;
let lastConnectArgs = null; // { userId, accessToken }

// Pub/sub por tipo de mensaje: type -> Set<handler(data)>
const listeners = new Map();

// Suscripción activa a la lista de casos (Home). Se reenvía al reconectar.
// El WS entrega las respuestas en orden, así que la última aplicada es la de los filtros vigentes.
let activeCaseListFilters = null;

function buildWsUrl(userId, accessToken) {
  const qs = new URLSearchParams();
  if (userId) qs.set("u", userId);
  if (accessToken) qs.set("t", accessToken);

  // NOTE: This keeps your existing "u/t" convention. If you can, prefer
  // passing tokens via a first message after connect instead of query params.
  const query = qs.toString();
  return `${WS_BASE_URL}${"/web"}?${query}`;
}

/** Latest credentials from session (e.g. after token refresh elsewhere). */
function getAuthPairFromSession() {
  const token = auth.getToken();
  const user = auth.getUser();
  if (token && user?._id) {
    return { userId: user._id, accessToken: token };
  }
  return null;
}

function emit(type, data) {
  const handlers = listeners.get(type);
  if (!handlers) return;
  handlers.forEach((handler) => {
    try {
      handler(data);
    } catch (error) {
      console.error(`WebSocket handler error (${type}):`, error);
    }
  });
}

function isOpen() {
  return socket?.readyState === WebSocket.OPEN;
}

function sendCaseListSubscribe() {
  return sendMessage({ type: "caseListSubscribe", data: activeCaseListFilters });
}

function scheduleReconnect() {
  if (manuallyClosed) return;
  if (reconnectTimer) return;

  const delay = Math.min(
    BASE_RECONNECT_MS * Math.pow(2, Math.min(reconnectAttempts, 12)),
    MAX_RECONNECT_DELAY_MS
  );

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    reconnectAttempts += 1;
    const pair = getAuthPairFromSession();
    if (!pair) {
      return;
    }
    lastConnectArgs = pair;
    connectSocket(pair.userId, pair.accessToken);
  }, delay);
}

export function connectSocket(userId, accessToken) {
  lastConnectArgs = { userId, accessToken };
  manuallyClosed = false;

  if (
    socket &&
    (socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING)
  ) {
    useSocketConnectionStore
      .getState()
      .setConnected(socket.readyState === WebSocket.OPEN);
    return socket;
  }

  const url = buildWsUrl(userId, accessToken);
  if (!url) {
    console.error("WebSocket URL is missing. Check REACT_APP_PUBLIC_WSS.");
    return null;
  }

  socket = new WebSocket(url);

  socket.addEventListener("open", () => {
    reconnectAttempts = 0;
    useSocketConnectionStore.getState().setConnected(true);
    console.log("WebSocket connected");
    if (activeCaseListFilters) sendCaseListSubscribe();
  });

  socket.addEventListener("message", (event) => {
    console.log("WebSocket message:", event?.data);

    try {
      const data =
        typeof event?.data === "string" ? JSON.parse(event.data) : event?.data;

      if (data?.type === "saleNotifInsert") {
        notify(`${data?.data?.title}  ${data?.data?.message}`, "notification");
      }
      if (data?.type === "serviceCountTv") {
        const payload = data?.data;
        if (payload && typeof payload._id !== "undefined") {
          useTvSectionStore.getState().updateProviderCount({
            _id: payload._id,
            totalActive: payload.totalActive,
            totalDone: payload.totalDone,
          });
        }
      }
      // Respuesta que llega después de haber salido de la vista
      if (data?.type === "caseListSubscribe" && !activeCaseListFilters) return;
      if (data?.type) emit(data.type, data.data);
    } catch {
      // Ignore parse errors for non-JSON messages
    }
  });

  socket.addEventListener("close", (event) => {
    useSocketConnectionStore.getState().setConnected(false);
    console.log("WebSocket disconnected:", {
      code: event?.code,
      reason: event?.reason,
      wasClean: event?.wasClean,
    });
    scheduleReconnect();
  });

  socket.addEventListener("error", () => {
    // Browser WS "error" event has very little detail by design.
    console.error("WebSocket error (no details available from browser).");
  });

  return socket;
}

export function disconnectSocket() {
  activeCaseListFilters = null;
  if (socket) {
    manuallyClosed = true;
    reconnectAttempts = 0;
    useSocketConnectionStore.getState().setConnected(false);
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    // 1000 = normal closure
    socket.close(1000, "client disconnect");
    socket = null;
  }
}

export function getSocket() {
  return socket;
}

/** Envía un mensaje si el socket está abierto. Devuelve true si se envió. */
export function sendMessage(message) {
  if (!isOpen()) return false;
  socket.send(JSON.stringify(message));
  return true;
}

/** Registra un handler para un tipo de mensaje. Devuelve la función para darse de baja. */
export function onMessage(type, handler) {
  if (!listeners.has(type)) listeners.set(type, new Set());
  listeners.get(type).add(handler);
  return () => {
    const handlers = listeners.get(type);
    if (!handlers) return;
    handlers.delete(handler);
    if (!handlers.size) listeners.delete(type);
  };
}

/**
 * Suscribe a las actualizaciones de la lista de casos con los filtros de la vista.
 * Si el socket aún no está abierto, se envía al conectar (y en cada reconexión).
 * Devuelve true si se envió en este momento.
 */
export function subscribeCaseList(filters = {}) {
  activeCaseListFilters = filters;
  return sendCaseListSubscribe();
}

export function unsubscribeCaseList() {
  if (activeCaseListFilters) {
    sendMessage({ type: "caseListUnsubscribe" });
  }
  activeCaseListFilters = null;
}
